import React, {
  ReactElement,
  Dispatch,
  SetStateAction,
  useState,
  useEffect,
  useRef,
} from 'react';
import type YouTubePlayer from 'youtube-player';
import { useNavigate } from 'react-router-dom';

import { shuffleArray, mod } from '~/utils';
import COLORS from '~/constants/colors';
import { FONT_SIZES, FONT_WEIGHTS } from '~/constants/typography';
import VARIANTS from '~/constants/variants';
import Button from '~/components/buttons/Button';
import Modal from '~/components/Modal';
import Title from '~/components/titles/Title';

import { Problem, Settings } from '../../models';
import uris from '../../uris';
import { GAME_MODES, INPUT_MODES } from '../../constants/defaults';
import { SONGS, HOMEWORKS } from '../../examples';
import { INDEX_MAP } from '../../constants/kana';
import { getVideoId, getWindowDims } from '../../helpers';
import Carousel, { useCarousel } from './components/Carousel';
import SongNumberInput from './components/SongNumberInput';
import SmallButton from './components/SmallButton';
import ShowPromo from './components/ShowPromo';
import PromoModal from './components/PromoModal';
import Shuffle from './components/Shuffle';
import InputModeFlipper from './components/InputModeFlipper';
import PadLabelsFlipper from './components/PadLabelsFlipper';
import GuidanceSwitch from './components/GuidanceSwitch';
import StartButton from './components/StartButton';
import OptionSelect from '../../components/OptionSelect';
import NoteVelocitySlider from '../../components/NoteVelocitySlider';
import PreviewPlayer from './components/PreviewPlayer';

interface MenuProps {
  settings: Settings;
  setSettings: Dispatch<SetStateAction<Settings>>;
  videoPlayer: YouTubePlayer;
}

function Menu({ settings, setSettings, videoPlayer }: MenuProps): ReactElement {
  const navigate = useNavigate();
  const canAlign =
    settings.rhythm.timings.length > 0 && settings.homework.problems.length > 0;
  const getKey = (type, idx) => `PagesFlowGoMenu${type}Option${idx}`;

  // Input pad settings and check homework answers are subset of labels
  const inputPadLabels = Object.keys(INDEX_MAP);
  const checkInclusion = (hw) =>
    hw.problems
      .map((problem) => problem.answers)
      .flat()
      .every((answer) => inputPadLabels.includes(answer));
  const answersIncluded = checkInclusion(settings.homework);
  const getSafeInputMode = (hw) =>
    checkInclusion(hw)
      ? settings.inputMode || INPUT_MODES.jog
      : INPUT_MODES.key;

  useEffect(
    () =>
      setSettings({
        ...settings,
        inputMode: getSafeInputMode(settings.homework),
      }),
    [answersIncluded]
  );

  // Interface relevant
  const songNumberRef = useRef<HTMLInputElement>(null);
  const [
    carouselIdx,
    setCarouselIdx,
    carouselHandlers,
    carouselContainerStyle,
  ] = useCarousel(SONGS.length, undefined, undefined, undefined, songNumberRef);
  const [showPreview, setShowPreview] = useState(false);
  const [showRhythmShoutout, setShowRhythmShoutout] = useState(false);
  const [showHomeworkShoutout, setShowHomeworkShoutout] = useState(false);
  const [previewPlayer, setPreviewPlayer] = useState<YouTubePlayer>();
  const startDisabled =
    settings.songIdx < 0 ||
    !settings.song.source ||
    settings.rhythm.timings.length === 0 ||
    !canAlign ||
    !videoPlayer;
  const handleHidePreview = () => setShowPreview(false);
  const previewWidth =
    getWindowDims().w - 0.7 * parseInt(FONT_SIZES.text, 10) * 3;
  let startButtonText = 'Flow GO!';

  if (!videoPlayer) startButtonText = 'Flow Loading...';
  else if (startDisabled) startButtonText = 'Flow Broken...';

  // Handle menu interactions
  const handleSongChoice = (idx, shouldSetCarouselIdx = true) => {
    const songIdx = mod(idx, SONGS.length);
    const song = SONGS[songIdx];

    if (shouldSetCarouselIdx) setCarouselIdx(songIdx);
    setSettings({ ...settings, songIdx, song, rhythm: song.rhythms[0] });
  };
  const handleRhythmChoice = (option) =>
    setSettings({ ...settings, rhythm: option.value });
  const handleHomeworkChoice = (option) =>
    setSettings({
      ...settings,
      homework: option.value,
      ...(!checkInclusion(option.value) && { inputMode: INPUT_MODES.key }),
    });

  useEffect(() => {
    if (settings.song.source !== SONGS[carouselIdx].source)
      handleSongChoice(carouselIdx, false);
  }, [carouselIdx]);

  // Handle starting game
  const alignHomework = (hw, r, shuffle) => {
    const ps: Problem[] = [];
    let i = r.timings.length;
    let j = 0;

    while (i > 0) {
      const problemIdx = j % hw.problems.length;
      const problem = hw.problems[problemIdx];

      if (i < problem.answers.length) problem.answers.slice(0, i);
      ps.push(problem);
      j += 1;
      i -= problem.answers.length;
    }

    return { ...hw, problems: shuffle ? shuffleArray(ps) : ps };
  };
  const handleStart = () => {
    if (!startDisabled) {
      const safeRhythm = {
        ...settings.rhythm,
        timings: settings.rhythm.timings.sort((a, b) => a - b),
      };
      const safeHomework = alignHomework(
        settings.homework,
        safeRhythm,
        settings.randomize
      );

      setSettings({ ...settings, rhythm: safeRhythm, homework: safeHomework });

      // Needed here for mobile not allowing autoplay
      videoPlayer.setVolume(100);
      setShowPreview(false); // Remove iFrame render to reduce lag maybe
      videoPlayer.loadVideoById(getVideoId(settings.song.source));
      navigate(uris.arcade, { state: { gameMode: GAME_MODES.play } });
    }
  };

  // Styling
  const previewModalStyle = {
    minWidth: `${Math.min(previewWidth, 480)}px`,
  };
  const slideByStyle = {
    fontWeight: FONT_WEIGHTS.thin,
  };
  const levelStyle = {
    display: 'table',
    margin: 'auto',
    marginTop: '0.25em',
  };
  const inputOptionLevelStyle = {
    ...levelStyle,
    marginTop: '0.38em',
  };
  const centerWingStyle = {
    display: 'table-cell',
    verticalAlign: 'middle',
    textAlign: 'center' as const,
  };
  const songNumberCenterWingStyle = {
    ...centerWingStyle,
    display: 'inline-flex',
  };
  const rightWingStyle = {
    ...centerWingStyle,
    width: 'min(10em, 13vw)',
    textAlign: 'left' as const,
  };
  const songNumberRightWingStyle = {
    ...rightWingStyle,
    display: 'inline-flex',
    width: 'min(10em, 25vw)',
  };
  const leftWingStyle = {
    ...rightWingStyle,
    textAlign: 'right' as const,
  };
  const songNumberLeftWingStyle = {
    ...leftWingStyle,
    width: 'min(10em, 25vw)',
  };
  const levelRowSpaceStyle = {
    height: '0.15em',
  };
  const shufflePlaceholderStyle = {
    display: 'inline-block',
    width: '2em',
    margin: '0 0.25em',
    paddingRight: '0.15em',
  };

  useEffect(() => {
    if (settings.songIdx < 0)
      setSettings({
        ...settings,
        songIdx: 0,
        song: SONGS[0],
        rhythm: SONGS[0].rhythms[0],
        homework: HOMEWORKS[0],
        inputMode: getSafeInputMode(HOMEWORKS[0]),
      });
    else if (carouselIdx !== settings.songIdx) setCarouselIdx(settings.songIdx);
  }, [SONGS, HOMEWORKS]);

  return (
    <div>
      <Carousel
        activeIdx={carouselIdx}
        setActiveIdx={handleSongChoice}
        handlers={carouselHandlers}
        containerStyle={carouselContainerStyle}
      >
        {SONGS.map((s, idx) => (
          <p key={getKey('CarouselSlide', idx)}>
            <i>{s.title}</i>
            <span style={slideByStyle}> by </span>
            <b>{s.artist}</b>
          </p>
        ))}
      </Carousel>
      <br />
      <div style={{ ...levelStyle, marginTop: '0.5em' }}>
        <div style={songNumberLeftWingStyle} />
        <div style={songNumberCenterWingStyle}>
          <SongNumberInput callback={handleSongChoice} ref={songNumberRef} />
        </div>
        <div style={songNumberRightWingStyle}>
          <SmallButton
            label="Preview"
            active={showPreview}
            callback={() => setShowPreview(true)}
          />
        </div>
      </div>
      <Title color={COLORS.white}>Rhythm</Title>
      <table style={levelStyle}>
        <tbody>
          <tr>
            <td style={leftWingStyle}>
              <ShowPromo
                label="🎉"
                setVisible={setShowRhythmShoutout}
                promoText={settings.rhythm.promoText}
                promoLink={settings.rhythm.promoLink}
              />
            </td>
            <td style={centerWingStyle}>
              <OptionSelect
                placeholder={
                  settings.song.rhythms.length > 0
                    ? 'Select a pattern...'
                    : 'No patterns available'
                }
                value={{
                  value: settings.rhythm,
                  label: settings.rhythm.name,
                }}
                onChange={handleRhythmChoice}
                options={settings.song.rhythms.map((r) => ({
                  value: r,
                  label: r.name,
                }))}
                isSearchable={false}
                controlShouldRenderValue={settings.song.rhythms.length > 0}
                disabled={settings.song.rhythms.length === 0}
              />
            </td>
            <td style={rightWingStyle}>
              <div style={shufflePlaceholderStyle} />
            </td>
          </tr>
          <tr style={levelRowSpaceStyle} />
          <tr style={levelRowSpaceStyle} />
          <tr>
            <td style={leftWingStyle} />
            <td style={centerWingStyle}>
              <NoteVelocitySlider
                velocity={settings.velocity}
                onChange={(velocity) => setSettings({ ...settings, velocity })}
              />
            </td>
            <td style={rightWingStyle} />
          </tr>
        </tbody>
      </table>
      <Title color={COLORS.white}>Homework</Title>
      <table style={levelStyle}>
        <tbody>
          <tr>
            <td style={leftWingStyle}>
              <ShowPromo
                label="💯"
                setVisible={setShowHomeworkShoutout}
                promoText={settings.homework.promoText}
                promoLink={settings.homework.promoLink}
              />
            </td>
            <td style={centerWingStyle}>
              <OptionSelect
                placeholder="Select an assignment..."
                value={{
                  value: settings.homework,
                  label: settings.homework.title,
                }}
                onChange={handleHomeworkChoice}
                options={HOMEWORKS.map((hw) => ({
                  value: hw,
                  label: hw.title,
                }))}
                isSearchable={false}
              />
            </td>
            <td style={rightWingStyle}>
              <Shuffle
                checked={settings.randomize}
                callback={() =>
                  setSettings({ ...settings, randomize: !settings.randomize })
                }
              />
            </td>
          </tr>
        </tbody>
      </table>
      <div style={inputOptionLevelStyle}>
        <InputModeFlipper
          inputMode={settings.inputMode}
          onChange={(m) => setSettings({ ...settings, inputMode: m })}
          answersIncluded={answersIncluded}
        />
        <PadLabelsFlipper
          which={settings.whichPadLabels}
          onChange={(l) => setSettings({ ...settings, whichPadLabels: l })}
          inputMode={settings.inputMode}
        />
        <GuidanceSwitch
          label="Hints"
          guidance={settings.guidance}
          setGuidance={() =>
            setSettings({ ...settings, guidance: !settings.guidance })
          }
          inputMode={settings.inputMode}
        />
      </div>
      <br />
      <StartButton
        label={startButtonText}
        disabled={startDisabled}
        callback={handleStart}
      />
      <Modal
        visible={showPreview}
        hider={handleHidePreview}
        style={previewModalStyle}
      >
        <PreviewPlayer
          width={previewWidth}
          settings={settings}
          showPreview={showPreview}
          previewPlayer={previewPlayer}
          setPreviewPlayer={setPreviewPlayer}
        />
        <br />
        <Button
          label="Noice"
          width="100%"
          variant={VARIANTS.warning}
          callback={handleHidePreview}
        />
      </Modal>
      <PromoModal
        visible={showRhythmShoutout}
        setVisible={setShowRhythmShoutout}
        title="Rhythm Recorder's Shoutout!"
        promoText={settings.rhythm.promoText}
        promoLink={settings.rhythm.promoLink}
        buttonLabel="So True"
      />
      <PromoModal
        visible={showHomeworkShoutout}
        setVisible={setShowHomeworkShoutout}
        title="Homework Maker's Shoutout!"
        promoText={settings.homework.promoText}
        promoLink={settings.homework.promoLink}
        buttonLabel="Poggers"
      />
    </div>
  );
}

export default Menu;
